import { z } from "zod";
import { AttachmentRequirementDisplaySchema } from "@/services/shared/attachment-requirement.types";
import {
	ColumnValueSchema,
	DataQuerySchema,
	FormDefinitionSchema,
} from "../form-definition/form-definition-core.types";
import {
	SimpleAddressSchema,
	SubOrganizationDisplaySchema,
} from "../organization/organization.types";

export {
	type CreateNoteInfo,
	CreateNoteInfoSchema,
	type NoteDisplay,
	NoteDisplayArraySchema,
	NoteDisplaySchema,
	type NoteParams,
	NoteParamsSchema,
} from "../shared/note.types";

// ==================== BASE SCHEMAS ====================

/**
 * User info light schema (for ticket user references)
 */
export const UserInfoLightSchema = z.object({
	id: z.string().uuid().nullable().optional(),
	hasAvatar: z.boolean().optional(),
	displayName: z.string().nullable().optional(),
	email: z.string().nullable().optional(),
	firstName: z.string().nullable().optional(),
	middleName: z.string().nullable().optional(),
	lastName: z.string().nullable().optional(),
});

/**
 * Location Point schema
 */
export const PointSchema = z.object({
	coordinates: z
		.object({
			altitude: z.number().optional().nullable(),
			latitude: z.number().optional().nullable(),
			longitude: z.number().optional().nullable(),
		})
		.optional()
		.nullable(),
	boundingBoxes: z.array(z.unknown()).optional().nullable(),
	crs: z.string().optional().nullable(),
});

/**
 * Ticket status display schema
 * Based on GET /tickets/{organizationId}/valid-statuses
 */
export const TicketStatusDisplaySchema = z.object({
	id: z.string().uuid(),
	name: z.string(),
	description: z.string().nullable().optional(),
	isInitialStatus: z.boolean().optional(),
});

/**
 * Ticket statuses array schema
 */
export const TicketStatusesArraySchema = z.array(TicketStatusDisplaySchema);

/**
 * Nested `statusLifecycle` on GET /tickets/types/{id} (and list rows when present).
 */
const TicketTypeEmbeddedStatusLifecycleSchema = z
	.object({
		id: z.string().uuid(),
		name: z.string().nullable().optional(),
		organizationId: z.string().uuid().nullable().optional(),
	})
	.passthrough();

/**
 * OpenAPI `TicketTypeDisplay` — GET /tickets/types/{start}/{count}, GET /tickets/types/{id}
 *
 * Preprocess:
 * - Normalizes misspelled `ticketTypeSubOrganizaitons` from the API.
 * - Denormalizes `lifecycleId` / `lifecycleName` from nested `statusLifecycle` when the API
 *   only returns the embedded object.
 */
const TicketTypeDisplayCoreSchema = z.object({
	id: z.string().uuid(),
	name: z.string().nullable(),
	description: z.string().nullable().optional(),
	icon: z.string().nullable().optional(),
	ticketTypeSubOrganizations: z.array(SubOrganizationDisplaySchema).default([]),
	statusLifecycle:
		TicketTypeEmbeddedStatusLifecycleSchema.nullable().optional(),
	lifecycleId: z.string().nullable().optional(),
	lifecycleName: z.string().nullable().optional(),
	/** Denormalized from API for list/detail display */
	statusLifecycleName: z.string().nullable().optional(),
	source: z.string().nullable().optional(),
	/** When true, ticket type is a system/default type; when false, custom */
	defaultType: z.boolean().optional(),
	/** Whether this type is enabled for the organization */
	enabled: z.boolean().optional(),
	createdAt: z.string().nullable().optional(),
	updatedAt: z.string().nullable().optional(),
	createdBy: z.string().uuid().nullable().optional(),
	updatedBy: z.string().uuid().nullable().optional(),
	/** Display name supplied directly by the backend — no secondary lookup needed */
	createdByUser: z.string().nullable().optional(),
	updatedByUser: z.string().nullable().optional(),
	locationRequired: z.boolean().optional(),
	attachmentRequirement: AttachmentRequirementDisplaySchema.nullable().optional(),
	hasForm: z.boolean().optional(),
});

export const TicketTypeDisplaySchema = z.preprocess((val) => {
	if (val && typeof val === "object" && !Array.isArray(val)) {
		const o = val as Record<string, unknown>;
		const rawSubs =
			o.ticketTypeSubOrganizaitons ?? o.ticketTypeSubOrganizations;
		const embedded = o.statusLifecycle;
		const embeddedId =
			embedded &&
			typeof embedded === "object" &&
			embedded !== null &&
			typeof (embedded as { id?: unknown }).id === "string"
				? (embedded as { id: string }).id
				: undefined;
		const embeddedName =
			embedded && typeof embedded === "object" && embedded !== null
				? ((embedded as { name?: string | null }).name ?? undefined)
				: undefined;

		const lifecycleId =
			typeof o.lifecycleId === "string" ? o.lifecycleId : embeddedId;

		const lifecycleName =
			(o.lifecycleName as string | null | undefined) ?? embeddedName;

		return {
			...o,
			ticketTypeSubOrganizations: Array.isArray(rawSubs) ? rawSubs : [],
			...(lifecycleId !== undefined ? { lifecycleId } : {}),
			...(lifecycleName !== undefined ? { lifecycleName } : {}),
		};
	}
	return val;
}, TicketTypeDisplayCoreSchema);

/**
 * Ticket types array schema
 */
export const TicketTypesArraySchema = z.array(TicketTypeDisplaySchema);

/**
 * Query parameters for GET /tickets/types/count, GET /tickets/types/{start}/{count}, export
 */
export const TicketTypesListParamsSchema = z.object({
	IncludeDeleted: z.boolean().optional(),
	CreatedBefore: z.string().optional(),
	CreatedAfter: z.string().optional(),
	UpdatedBefore: z.string().optional(),
	UpdatedAfter: z.string().optional(),
	OrganizationId: z.string().optional(),
	SubOrganizationId: z.string().optional(),
	ForOrganizationUser: z.string().uuid().optional(),
	Enabled: z.boolean().optional(),
	SearchTerm: z.string().optional(),
	"OrderBy.Fields": z.array(z.string()).optional(),
	"OrderBy.Descending": z.boolean().optional(),
});

export type TicketTypesListParams = z.infer<typeof TicketTypesListParamsSchema>;

/** Input for ticket type list queries — supports camelCase org aliases */
export type TicketTypesQueryOptionsInput = Partial<TicketTypesListParams> & {
	organizationId?: string;
	subOrganizationId?: string;
};

export function normalizeTicketTypesQueryOptions(
	input?: TicketTypesQueryOptionsInput,
): TicketTypesListParams {
	if (!input) return {};
	const { organizationId, subOrganizationId, ...rest } = input;
	const out: TicketTypesListParams = { ...rest };
	if (organizationId !== undefined && organizationId !== "") {
		out.OrganizationId = organizationId;
	}
	if (subOrganizationId !== undefined && subOrganizationId !== "") {
		out.SubOrganizationId = subOrganizationId;
	}
	return out;
}

/**
 * Create ticket type — POST /tickets/types
 * OpenAPI base fields plus optional lifecycle/source for newer APIs.
 */
export const CreateTicketTypeInfoSchema = z
	.object({
		organizationId: z.string().nullable().optional(),
		subOrganizationIds: z.array(z.string()).nullable().optional(),
		name: z.string().nullable().optional(),
		description: z.string().nullable().optional(),
		icon: z.string().nullable().optional(),
		updatable: z.boolean().nullable().optional(),
		source: z.enum(["system", "custom"]).optional(),
		statusLifecycleId: z.string().nullable().optional(),
	})
	.passthrough();

/**
 * Edit ticket type — PUT /tickets/types/{ticketTypeId}
 * OpenAPI `EditTicketTypeInfo`.
 */
export const EditTicketTypeInfoSchema = z.object({
	organizationId: z.string().nullable().optional(),
	subOrganizationIds: z.array(z.string()).nullable().optional(),
	typeId: z.string().nullable().optional(),
	name: z.string().nullable().optional(),
	description: z.string().nullable().optional(),
	icon: z.string().nullable().optional(),
	statusLifecycleId: z.string().nullable().optional(),
	locationRequired: z.boolean().optional(),
});

/**
 * Ticket priority display schema
 * Based on GET /tickets/priorities
 */
export const TicketPriorityDisplaySchema = z.object({
	id: z.string().uuid(),
	name: z.string(),
	description: z.string().nullable().optional(),
});

/**
 * Ticket priorities array schema
 */
export const TicketPrioritiesArraySchema = z.array(TicketPriorityDisplaySchema);

/**
 * Ticket Display schema
 * Based on GET /tickets/{start}/{count}
 */
export const TicketDisplaySchema = z.object({
	id: z.string().uuid(),
	organizationId: z.string().uuid(),
	ticketNumber: z.number().int(),
	name: z.string().nullable().optional(),
	description: z.string().nullable().optional(),
	statusId: z.string().uuid(),
	statusName: z.string().nullable().optional(),
	statusCategoryName: z.string().nullable().optional(),
	statusCategoryIcon: z.string().nullable().optional(),
	statusCategoryIconColor: z.string().nullable().optional(),
	typeId: z.string().uuid(),
	typeName: z.string().nullable().optional(),
	typeIcon: z.string().nullable().optional(),
	priorityId: z.string().uuid(),
	priorityName: z.string().nullable().optional(),
	subOrganizationId: z.string().uuid().nullable().optional(),
	location: PointSchema.nullable().optional(),
	geometrySource: z.string().nullable().optional(),
	gpsMethod: z.string().nullable().optional(),
	recordingDeviceData: z.unknown().nullable().optional(),
	entityAttachmentRequirementId: z.string().uuid().nullable().optional(),
	dueDate: z.string().datetime().nullable().optional(),
	userAssignmentCount: z.number().int().optional(),
	subContractorCount: z.number().int().optional(),
	createdAt: z.string().datetime().nullable().optional(),
	createdAtOffset: z.string().nullable().optional(),
	updatedAt: z.string().datetime().nullable().optional(),
	updatedAtOffset: z.string().nullable().optional(),
	data: z.array(ColumnValueSchema).nullable().optional(),
	/** Opaque ticket-type form values when API returns them (prefer `data` for hydrate). */
	formData: z
		.union([z.record(z.string(), z.unknown()), z.string()])
		.nullable()
		.optional(),
	formDefinition: FormDefinitionSchema.nullable().optional(),
	assignedUsers: z
		.array(z.lazy(() => TicketAssignmentDisplaySchema))
		.optional(),
	assignedSubContractors: z
		.array(z.lazy(() => TicketSubContractorDisplaySchema))
		.optional(),
	/**
	 * Related assets (OpenAPI TicketDisplay.assets → AssetDisplay[]).
	 * Typed loosely to avoid a ticket↔asset circular import.
	 */
	assets: z.array(z.unknown()).nullable().optional(),
	/**
	 * Related records (OpenAPI TicketDisplay.records → RecordDisplay[]).
	 * Typed loosely to avoid a ticket↔record circular import.
	 */
	records: z.array(z.unknown()).nullable().optional(),
	assignedTicketSubContractor: z.string().nullable().optional(),
	assignedTicketUser: z.string().nullable().optional(),
});

/**
 * Ticket Display with Users schema
 * Based on GET /tickets/{id}
 */
export const TicketDisplayWithUsersSchema = TicketDisplaySchema.extend({
	createdBy: UserInfoLightSchema.nullable().optional(),
	updatedBy: UserInfoLightSchema.nullable().optional(),
});

/**
 * Bounding box schema for geographic filtering
 */
export const BoundingBoxSchema = z.object({
	MaxLatitude: z.number().optional(),
	MinLatitude: z.number().optional(),
	MaxLongitude: z.number().optional(),
	MinLongitude: z.number().optional(),
});

/**
 * Ticket Parameters schema
 * Used for filtering and querying tickets
 */
export const TicketParamsSchema = z.object({
	BoundingBox: BoundingBoxSchema.optional(),
	HasCoordinates: z.boolean().optional(),
	IncludeDeleted: z.boolean().optional(),
	OrganizationId: z.string().uuid().optional(),
	SubOrganizationId: z.string().uuid().optional(),
	RelatedToAssetId: z.string().uuid().optional(),
	RelatedToRecordId: z.string().uuid().optional(),
	"OrderBy.Fields": z.array(z.string()).optional(),
	"OrderBy.Descending": z.boolean().optional(),
	SearchTerm: z.string().optional(),
	EntityDataQueries: z.array(DataQuerySchema).optional(),
});

/**
 * Create Ticket Info schema
 * Based on POST /tickets request body
 */
export const CreateTicketInfoSchema = z.object({
	organizationId: z.string().nullable().optional(),
	name: z.string().nullable().optional(),
	description: z.string().nullable().optional(),
	typeId: z.string().nullable().optional(),
	priorityId: z.string().nullable().optional(),
	dueDate: z.string().nullable().optional(),
	latitude: z.number().nullable().optional(),
	longitude: z.number().nullable().optional(),
	geometrySource: z.string().nullable().optional(),
	gpsMethod: z.string().nullable().optional(),
	subOrganizationId: z.string().nullable().optional(),
	subcontractorIds: z.array(z.string()).nullable().optional(),
	organizationUserIds: z.array(z.string()).nullable().optional(),
	/** Opaque ticket-type form values (OpenAPI CreateTicketInfo.formData). */
	formData: z.record(z.string(), z.unknown()).nullable().optional(),
});

/**
 * Ticket-from-text request schema
 * Based on POST /tickets/from-text and POST /tickets/from-text/preview request body
 */
export const TicketFromTextRequestSchema = z.object({
	organizationId: z.string().nullable().optional(),
	text: z.string().min(1),
	subOrganizationId: z.string().nullable().optional(),
	additionalPrompt: z.string().nullable().optional(),
});

/**
 * Draft produced by ticket-from-text (IDs only; names resolved client-side).
 */
export const TicketFromTextDraftSchema = z.object({
	typeId: z.string().nullable().optional(),
	priorityId: z.string().nullable().optional(),
	subOrganizationId: z.string().nullable().optional(),
	name: z.string().nullable().optional(),
	description: z.string().nullable().optional(),
	dueDate: z.string().nullable().optional(),
	latitude: z.number().nullable().optional(),
	longitude: z.number().nullable().optional(),
	organizationUserIds: z.array(z.string()).nullable().optional(),
	formData: z.record(z.string(), z.unknown()).nullable().optional(),
});

/**
 * POST /tickets/from-text/preview response
 */
export const TicketFromTextPreviewSchema = z.object({
	draft: TicketFromTextDraftSchema,
	warnings: z.array(z.string()).nullable().optional(),
	llmUsed: z.boolean().nullable().optional(),
	cached: z.boolean().nullable().optional(),
});

/**
 * POST /tickets/from-text response
 */
export const TicketFromTextCreateSchema = z.object({
	ticketId: z.string().nullable().optional(),
	draft: TicketFromTextDraftSchema,
	warnings: z.array(z.string()).nullable().optional(),
	llmUsed: z.boolean().nullable().optional(),
});

/**
 * Edit Ticket Info schema
 * Based on PUT /tickets/{ticketId} request body
 */
export const EditTicketInfoSchema = z.object({
	organizationId: z.string().nullable().optional(),
	name: z.string().nullable().optional(),
	description: z.string().nullable().optional(),
	statusId: z.string().nullable().optional(),
	typeId: z.string().nullable().optional(),
	priorityId: z.string().nullable().optional(),
	latitude: z.number().nullable().optional(),
	longitude: z.number().nullable().optional(),
	geometrySource: z.string().nullable().optional(),
	gpsMethod: z.string().nullable().optional(),
	subOrganizationId: z.string().nullable().optional(),
	organizationUserIds: z.array(z.string()).nullable().optional(),
	subContractorIds: z.array(z.string()).nullable().optional(),
	dueDate: z.string().nullable().optional(),
	/** Opaque ticket-type form values (OpenAPI EditTicketInfo.formData). */
	formData: z.record(z.string(), z.unknown()).nullable().optional(),
});

/**
 * Update Ticket Status Info schema
 * Based on PUT /tickets/{ticketId}/status request body
 */
export const UpdateTicketStatusInfoSchema = z.object({
	organizationId: z.string().nullable().optional(),
	statusId: z.string().nullable().optional(),
});

/**
 * Very Limited Organization User With User Display schema
 */
export const VeryLimitedOrganizationUserWithUserDisplaySchema = z.object({
	email: z.string().nullable().optional(),
	firstName: z.string().nullable().optional(),
	middleName: z.string().nullable().optional(),
	lastName: z.string().nullable().optional(),
	roles: z.string().nullable().optional(),
	id: z.string(),
	userId: z.string(),
	hasAvatar: z.boolean().optional(),
	displayName: z.string(),
	title: z.string().nullable().optional(),
	phone: z.string().nullable().optional(),
});

/**
 * Ticket Assignment Display schema
 * Based on GET /tickets/{ticketId}/assignments/{start}/{count}
 */
export const TicketAssignmentDisplaySchema = z.object({
	id: z.string().uuid(),
	ticketId: z.string().uuid(),
	organizationUserId: z.string().uuid(),
	organizationUser:
		VeryLimitedOrganizationUserWithUserDisplaySchema.nullable().optional(),
	createdBy: UserInfoLightSchema.nullable().optional(),
	updatedBy: UserInfoLightSchema.nullable().optional(),
});

/**
 * Ticket Assignment Parameters schema
 */
export const TicketAssignmentParamsSchema = z.object({
	TicketId: z.string().uuid().optional(),
	OrganizationUserId: z.string().uuid().optional(),
	OrganizationId: z.string().uuid().optional(),
	SubOrganizationId: z.string().uuid().optional(),
	"OrderBy.Fields": z.array(z.string()).optional(),
	"OrderBy.Descending": z.boolean().optional(),
});

/**
 * Sub-Contractor info schema (nested in ticket sub-contractor assignment)
 */
export const TicketSubContractorInfoSchema = z.object({
	id: z.string(),
	name: z.string().nullable().optional(),
	phone: z.string().nullable().optional(),
	totalAssignedUserCount: z.number().int().nullable().optional(),
	statusCode: z.number().int().nullable().optional(),
	address: SimpleAddressSchema.nullable().optional(),
	created: z.string().datetime().nullable().optional(),
	lastUpdated: z.string().datetime().nullable().optional(),
});
// .passthrough();

/**
 * Ticket Sub-Contractor Display schema (assignment record)
 * Based on GET /tickets/{ticketId}/sub-contractors/{start}/{count}
 */
export const TicketSubContractorDisplaySchema = z.object({
	id: z.string().uuid(),
	ticketId: z.string().uuid(),
	subContractor: TicketSubContractorInfoSchema.nullable().optional(),
	createdBy: UserInfoLightSchema.nullable().optional(),
	updatedBy: UserInfoLightSchema.nullable().optional(),
});
// .passthrough();

/**
 * Sub-Contractor Parameters schema
 */
export const SubContractorParamsSchema = z.object({
	TicketId: z.string().uuid().optional(),
	SubContractorId: z.string().uuid().optional(),
	OrganizationId: z.string().uuid().optional(),
	SubOrganizationId: z.string().uuid().optional(),
	"OrderBy.Fields": z.array(z.string()).optional(),
	"OrderBy.Descending": z.boolean().optional(),
});

/**
 * Organization display schema (simplified for audit log)
 */
export const LimitedOrganizationDisplaySchema = z.object({
	id: z.string().uuid().nullable().optional(),
	name: z.string().nullable().optional(),
});

/**
 * Audit log display schema
 * Based on GET /tickets/{id}/history/{start}/{count}
 */
export const AuditLogDisplaySchema = z.object({
	id: z.string().uuid(),
	organizationId: z.string().nullable().optional(),
	userId: z.string().nullable().optional(),
	action: z.string().nullable().optional(),
	date: z.string().datetime().nullable().optional(),
	source: z.string().nullable().optional(),
	entityType: z.string().nullable().optional(),
	entityId: z.string().nullable().optional(),
	entityDisplay: z.string().nullable().optional(),
	fieldName: z.string().nullable().optional(),
	sessionId: z.string().nullable().optional(),
	oldValue: z.string().nullable().optional(),
	newValue: z.string().nullable().optional(),
	userAgent: z.string().nullable().optional(),
	userIpAddress: z.string().nullable().optional(),
	user: UserInfoLightSchema.nullable().optional(),
	organization: LimitedOrganizationDisplaySchema.nullable().optional(),
});

/**
 * Feature Collection schema (GeoJSON)
 * Based on GET /tickets/features/{start}/{count}
 */
export const FeatureCollectionSchema = z.object({
	type: z.literal("FeatureCollection"),
	features: z.array(
		z.object({
			type: z.literal("Feature"),
			geometry: z.unknown(),
			properties: z.unknown(),
		}),
	),
});

// ==================== TYPE EXPORTS ====================

export type UserInfoLight = z.infer<typeof UserInfoLightSchema>;
export type Point = z.infer<typeof PointSchema>;
export type TicketStatusDisplay = z.infer<typeof TicketStatusDisplaySchema>;
export type GetTicketStatusesResponse = TicketStatusDisplay[];
export type TicketTypeDisplay = z.infer<typeof TicketTypeDisplaySchema>;
/** Alias for admin tables (matches JobType naming) */
export type TicketType = TicketTypeDisplay;
export type GetTicketTypesResponse = TicketTypeDisplay[];
export type CreateTicketTypeInfo = z.infer<typeof CreateTicketTypeInfoSchema>;
export type EditTicketTypeInfo = z.infer<typeof EditTicketTypeInfoSchema>;
export type TicketPriorityDisplay = z.infer<typeof TicketPriorityDisplaySchema>;
export type GetTicketPrioritiesResponse = TicketPriorityDisplay[];
export type TicketDisplay = z.infer<typeof TicketDisplaySchema>;
export type TicketDisplayWithUsers = z.infer<
	typeof TicketDisplayWithUsersSchema
>;
export type BoundingBox = z.infer<typeof BoundingBoxSchema>;
export type TicketParams = z.infer<typeof TicketParamsSchema>;
export type CreateTicketInfo = z.infer<typeof CreateTicketInfoSchema>;
export type TicketFromTextRequest = z.infer<typeof TicketFromTextRequestSchema>;
export type TicketFromTextDraft = z.infer<typeof TicketFromTextDraftSchema>;
export type TicketFromTextPreview = z.infer<typeof TicketFromTextPreviewSchema>;
export type TicketFromTextCreate = z.infer<typeof TicketFromTextCreateSchema>;
export type EditTicketInfo = z.infer<typeof EditTicketInfoSchema>;
export type UpdateTicketStatusInfo = z.infer<
	typeof UpdateTicketStatusInfoSchema
>;
export type VeryLimitedOrganizationUserWithUserDisplay = z.infer<
	typeof VeryLimitedOrganizationUserWithUserDisplaySchema
>;
export type TicketAssignmentDisplay = z.infer<
	typeof TicketAssignmentDisplaySchema
>;
export type TicketAssignmentParams = z.infer<
	typeof TicketAssignmentParamsSchema
>;
export type TicketSubContractorInfo = z.infer<
	typeof TicketSubContractorInfoSchema
>;
export type TicketSubContractorDisplay = z.infer<
	typeof TicketSubContractorDisplaySchema
>;
export type SubContractorParams = z.infer<typeof SubContractorParamsSchema>;
export type LimitedOrganizationDisplay = z.infer<
	typeof LimitedOrganizationDisplaySchema
>;
export type AuditLogDisplay = z.infer<typeof AuditLogDisplaySchema>;
export type FeatureCollection = z.infer<typeof FeatureCollectionSchema>;

// ==================== TICKET ASSET / RECORD ASSOCIATIONS ====================

/**
 * OpenAPI `TicketAssetInfo` — junction row for ticket↔asset links.
 */
export const TicketAssetInfoSchema = z.object({
	id: z.string().nullable().optional(),
	serverCreatedAt: z.string().nullable().optional(),
	serverUpdatedAt: z.string().nullable().optional(),
	organizationId: z.string().uuid().nullable().optional(),
	ticketId: z.string().uuid().nullable().optional(),
	assetId: z.string().uuid().nullable().optional(),
});

export const TicketAssetInfoArraySchema = z.array(TicketAssetInfoSchema);

/**
 * OpenAPI `TicketRecordInfo` — junction row for ticket↔record links.
 */
export const TicketRecordInfoSchema = z.object({
	id: z.string().nullable().optional(),
	serverCreatedAt: z.string().nullable().optional(),
	serverUpdatedAt: z.string().nullable().optional(),
	organizationId: z.string().uuid().nullable().optional(),
	ticketId: z.string().uuid().nullable().optional(),
	recordId: z.string().uuid().nullable().optional(),
});

export const TicketRecordInfoArraySchema = z.array(TicketRecordInfoSchema);

/**
 * Query filters for ticket asset association list / count endpoints.
 */
export const TicketAssetAssociationParamsSchema = z.object({
	TicketId: z.string().optional(),
	AssetId: z.string().optional(),
	OrganizationUserId: z.string().optional(),
	IncludeDeleted: z.boolean().optional(),
	CreatedBefore: z.string().optional(),
	CreatedAfter: z.string().optional(),
	UpdatedBefore: z.string().optional(),
	UpdatedAfter: z.string().optional(),
	OrganizationId: z.string().optional(),
	SubOrganizationId: z.string().optional(),
	ForOrganizationUser: z.string().optional(),
	OrderBy: z
		.object({
			Fields: z.array(z.string()).optional(),
			Descending: z.boolean().optional(),
		})
		.optional(),
});

/**
 * Query filters for ticket record association list / count endpoints.
 */
export const TicketRecordAssociationParamsSchema = z.object({
	TicketId: z.string().optional(),
	RecordId: z.string().optional(),
	OrganizationUserId: z.string().optional(),
	IncludeDeleted: z.boolean().optional(),
	CreatedBefore: z.string().optional(),
	CreatedAfter: z.string().optional(),
	UpdatedBefore: z.string().optional(),
	UpdatedAfter: z.string().optional(),
	OrganizationId: z.string().optional(),
	SubOrganizationId: z.string().optional(),
	ForOrganizationUser: z.string().optional(),
	OrderBy: z
		.object({
			Fields: z.array(z.string()).optional(),
			Descending: z.boolean().optional(),
		})
		.optional(),
});

export type TicketAssetInfo = z.infer<typeof TicketAssetInfoSchema>;
export type TicketRecordInfo = z.infer<typeof TicketRecordInfoSchema>;
export type TicketAssetAssociationParams = z.infer<
	typeof TicketAssetAssociationParamsSchema
>;
export type TicketRecordAssociationParams = z.infer<
	typeof TicketRecordAssociationParamsSchema
>;

// ==================== TICKET FILES ====================

/**
 * OpenAPI `FileDisplay` — GET /tickets/{ticketId}/files/{start}/{count}
 */
export const FileDisplaySchema = z.object({
	id: z.string().uuid(),
	/** Underlying FileInfo id — use for GET/DELETE/HEAD/PATCH/thumbnail. */
	fileInfoId: z.string().uuid(),
	name: z.string().nullable().optional(),
	description: z.string().nullable().optional(),
	size: z.number().int().nullable().optional(),
	createdAt: z.string().nullable().optional(),
	createdByUser: z.string().nullable().optional(),
	entityAttachmentRequirementTagId: z.string().uuid().nullable().optional(),
});

export const FileDisplayArraySchema = z.array(FileDisplaySchema);

/**
 * OpenAPI `EditTicketFileInfo` — PUT /tickets/{ticketId}/files/{ticketFileId}
 */
export const EditTicketFileInfoSchema = z.object({
	name: z.string().nullable().optional(),
	description: z.string().nullable().optional(),
});

/**
 * Query filters for ticket file list / count endpoints.
 */
export const TicketFileParamsSchema = z.object({
	SearchTerm: z.string().optional(),
	IncludeDeleted: z.boolean().optional(),
	OrganizationId: z.string().optional(),
	SubOrganizationId: z.string().optional(),
	OrderBy: z
		.object({
			Fields: z.array(z.string()).optional(),
			Descending: z.boolean().optional(),
		})
		.optional(),
});

export type FileDisplay = z.infer<typeof FileDisplaySchema>;
export type EditTicketFileInfo = z.infer<typeof EditTicketFileInfoSchema>;
export type TicketFileParams = z.infer<typeof TicketFileParamsSchema>;

/** Alias for note list params (shared NoteParams). */
export type TicketNoteParams = import("../shared/note.types").NoteParams;

// ==================== ERROR TYPES ====================

/**
 * Custom error class for ticket-related API errors
 */
export class TicketApiError extends Error {
	constructor(
		message: string,
		public readonly cause?: unknown,
		public readonly statusCode?: number,
	) {
		super(message);
		this.name = "TicketApiError";
		if (Error.captureStackTrace) {
			Error.captureStackTrace(this, TicketApiError);
		}
	}

	static fromServiceResult(result: {
		success: boolean;
		message?: string | null;
	}) {
		return new TicketApiError(
			result.message || "Ticket operation failed",
			result,
		);
	}
}

// ==================== SERVICE RESULT ====================

/**
 * Service result schema — accepts legacy (success / message) and newer
 * (isSuccess / errorMessages) shapes from the API.
 */
export const ServiceResultSchema = z
	.object({
		success: z.boolean().optional(),
		isSuccess: z.boolean().optional(),
		message: z.string().nullable().optional(),
		errorMessages: z.array(z.string()).nullable().optional(),
		affectedId: z.string().uuid().nullable().optional(),
	})
	.transform((raw) => ({
		success: raw.success ?? raw.isSuccess ?? false,
		message:
			raw.message ??
			(raw.errorMessages?.length ? raw.errorMessages.join("; ") : null),
		affectedId: raw.affectedId,
	}));

export type ServiceResult = z.output<typeof ServiceResultSchema>;
